import React, { useState } from 'react';
import classes from './Profile.module.scss';
import {
  Avatar,
  Typography,
  Grid,
  Form,
  Switch,
  Modal,
  message,
  Segmented
} from 'antd';
import { useTranslation } from 'react-i18next';
import { UserOutlined } from '@ant-design/icons';
import moment from 'moment/moment';
import Button from '../../components/UI/Button/Button';
import PasswordInput from '../../components/UI/Input/PasswordInput';
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { changePassword, updateMarketConsent } from '../../http/user';
import { useDispatch } from 'react-redux';
import EditAddressModal from '../../components/modals/EditAddress/EditAddressModal';

const UserInfo = ({ user, changeUserLanguage }) => {
  const breakpoints = Grid.useBreakpoint();
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const [messageApi, contextHolder] = message.useMessage();

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [formIsValid, setIsFormValid] = useState(false);

  const [addressModalOpen, setAddressModalOpen] = useState(false);

  const onClose = () => {
    setPasswordModalOpen(false);
    form.resetFields();
  };

  const onOpen = () => {
    setPasswordModalOpen(true);
  };

  const onSwitchChange = (checked) => {
    try {
      dispatch(updateMarketConsent(checked));
    } catch (error) {
      message.error(t('errors.swr'));
    }
  };

  const onSubmit = async (data) => {
    try {
      setPasswordLoading(true);
      await changePassword(data.oldPassword, data.password);
      messageApi.success(t('modals.changePassword.success'));
      onClose();
    } catch (error) {
      console.log(error);
      const msg = error?.message;
      if (msg === 'Current password is incorrect')
        messageApi.error(t('schema.password.oldPassword'));
      else if (msg === 'New password must differ from current password')
        messageApi.error(msg);
      else
        messageApi.error(msg || t('modals.changePassword.error'));
    } finally {
      setPasswordLoading(false);
    }
  };

  const onChange = () => {
    setIsFormValid(
      form.getFieldsError().every((field) => !field.errors.length)
    );
  };

  const onLanguageChange = (value) => {
    dispatch({ type: 'App/SET_LANG', payload: value.toLowerCase() });
    changeUserLanguage(value.toLowerCase());
    dispatch({ type: 'User/UPDATE', payload: { language: value.toLowerCase() } });
    i18n.changeLanguage(value.toLowerCase());
  };

  return (
    <>
      {contextHolder}
      <Modal open={passwordModalOpen} footer={null} onCancel={onClose}>
        {passwordLoading && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              zIndex: 1000
            }}
          >
            <Spin
              indicator={<LoadingOutlined spin />}
              style={{ height: 'fit-content' }}
            />
          </div>
        )}

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            alignItems: 'center',
            marginTop: 70,
            width: '100%'
          }}
        >
          <h2 style={{ textAlign: 'center' }}>
            {t('modals.changePassword.title')}
          </h2>
          <Form
            form={form}
            layout='vertical'
            onFinish={onSubmit}
            style={{ marginTop: 40, width: '100%' }}
            onFieldsChange={onChange}
          >
            <Form.Item
              name='oldPassword'
              rules={[{ required: true, message: t('schema.required') }]}
            >
              <PasswordInput placeholder={t('input.oldPassword')} />
            </Form.Item>
            <Form.Item
              name='password'
              rules={[
                { required: true, message: t('schema.required') },
                () => ({
                  validator(_, value) {
                    if (
                      value &&
                      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@"':;#$%^&*?,()\\/!-_+=[\]]{8,}$/.test(
                        value
                      )
                    ) {
                      return Promise.reject(t('schema.password.invalid'));
                    }
                    return Promise.resolve();
                  }
                })
              ]}
            >
              <PasswordInput placeholder={t('input.password')} />
            </Form.Item>
            <Form.Item
              name='repeatPassword'
              rules={[
                { required: true, message: t('schema.required') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error(t('schema.password.match'))
                    );
                  }
                })
              ]}
            >
              <PasswordInput placeholder={t('input.confirmPassword')} />
            </Form.Item>

            <Button
              wide
              style={{ marginBottom: 20 }}
              disabled={!formIsValid}
              onClick={form.submit}
            >
              {t('modals.changePassword.button')}
            </Button>
          </Form>
        </div>
      </Modal>

      <div className={classes.profile}>
        <div className={classes.avatarContainer}>
          <Avatar
            shape='square'
            style={{ flexShrink: 0 }}
            size={{ xs: 64, sm: 64, md: 64, lg: 128, xl: 128, xxl: 128 }}
            icon={<UserOutlined />}
          />

          <Segmented
            value={
              user.language?.charAt(0).toUpperCase() + user.language?.slice(1)
            }
            options={['Lv', 'En']}
            onChange={onLanguageChange}
          />
        </div>

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            flexGrow: 1,
            width: '100%',
            gap: 20,
            height: '100%'
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: breakpoints.xs ? 'column' : 'row',
              gap: 20
            }}
          >
            <Typography
              style={{
                fontWeight: 400,
                width: breakpoints.xs ? '100%' : '50%'
              }}
            >
              | <b>{t('pages.profile.firstname')}:</b>{' '}
              {user.firstname || t('etc.noData')}
            </Typography>
            <Typography
              style={{
                fontWeight: 400,
                width: breakpoints.xs ? '100%' : '50%'
              }}
            >
              | <b>{t('pages.profile.lastname')}:</b>{' '}
              {user.lastname || t('etc.noData')}
            </Typography>
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: breakpoints.xs ? 'column' : 'row',
              gap: 20
            }}
          >
            <Typography
              style={{
                fontWeight: 400,
                width: breakpoints.xs ? '100%' : '50%'
              }}
            >
              | <b>{t('pages.profile.dateOfBirth')}:</b>{' '}
              {user.dob ? moment(user.dob).format('LL') : t('etc.noData')}
            </Typography>
            <Typography
              style={{
                fontWeight: 400,
                width: breakpoints.xs ? '100%' : '50%'
              }}
            >
              | <b>{t('pages.profile.email')}:</b>{' '}
              {user.email || t('etc.noData')}
            </Typography>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              flexWrap: 'wrap'
            }}
          >
            <Typography style={{ fontWeight: 400 }}>
              | <b>{t('pages.profile.address')}:</b>{' '}
              {[user.country, user.city, user.street, user.house, user.flat, user.zip]
                .filter(Boolean)
                .join(', ') || t('etc.noData')}
            </Typography>
            <Button
              size="small"
              type="link"
              style={{ 
                padding: 5, 
                fontWeight: 500, 
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
              onClick={() => setAddressModalOpen(true)}
            >
              {[user.country, user.city, user.street, user.house, user.zip].some(Boolean)
                ? t('button.edit')
                : t('button.add')}
            </Button>
          </div>
          <Typography
            style={{
              fontWeight: 400
            }}
          >
            | <b>{t('pages.profile.id')}:</b> {user.id}
          </Typography>
          <div
            style={{
              display: 'flex',
              gap: 20,
              flexDirection: breakpoints.xs ? 'column' : 'row'
            }}
          >
            <Typography
              style={{
                fontWeight: 600,
                textDecoration: 'underline',
                width: breakpoints.xs ? '100%' : '50%',
                cursor: 'pointer'
              }}
              onClick={onOpen}
            >
              {t('modals.changePassword.title')}
            </Typography>
            <div
              style={{
                display: 'flex',
                gap: 20,
                width: breakpoints.xs ? '100%' : '50%'
              }}
            >
              <Switch checked={user.marketConsent} onChange={onSwitchChange} />
              <Typography style={{ fontSize: 18 }}>
                {t('pages.profile.marketConsent')}
              </Typography>
            </div>
          </div>
        </div>
      </div>

      <EditAddressModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        user={user}
      />

    </>
  );
};

export default UserInfo;